import { Button, ConfirmAction, DataTable, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AppEntity } from '../../api/apps.api';

interface AppListProps {
  apps: AppEntity[];
  loading: boolean;
  error: Error | null | undefined;
  onDelete: (id: string) => Promise<void>;
}

export function AppList({ apps, loading, error, onDelete }: AppListProps) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<AppEntity>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.status === 'ORPHAN' ? 'danger' : 'success'}>
            {row.original.status === 'ORPHAN' ? 'Orphan' : 'Active'}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'sandboxMode',
        header: 'Mode',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.sandboxMode ? 'warning' : 'success'}>
            {row.original.sandboxMode ? 'Sandbox' : 'Production'}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'lastSyncedAt',
        header: 'Last synced',
        cell: ({ row }) =>
          row.original.lastSyncedAt ? (
            <span className="text-sm">{new Date(row.original.lastSyncedAt).toLocaleString()}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.createdAt).toLocaleDateString()}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          row.original.status === 'ORPHAN' ? (
            <ConfirmAction
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Trash2 /> Delete
                </Button>
              }
              title="Delete this orphaned app?"
              description="This will permanently delete the app and revoke its API key. This cannot be undone."
              confirmLabel="Delete app"
              destructive
              onConfirm={() => onDelete(row.original.id)}
            />
          ) : null,
      },
    ],
    [onDelete],
  );

  return (
    <DataTable
      columns={columns}
      data={apps}
      isLoading={loading}
      error={error}
      onRowClick={(app) => navigate(`/apps/${app.id}`)}
    />
  );
}
